import {
    Box,
    Flex,
    HStack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Tag,
    VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStickyTableCopyFront } from '../../hooks/useStickyTableCopyFront';
import { stickyTableScrollCopySx, STICKY_TABLE_HEADER_Z_INDEX } from '../../utils/tableCopySx';
import { Icon as YaxleIcon, IconButton as YaxleIconButton, Tooltip } from '../../ui';
import { OrderedPart, OrderedTab, SupplierOrderStatus } from '../../hooks/useGetOrderDetail';
import { formatPercentage, formatPrice } from '../../functions';
import { getTableSettingsSx, TableSettings } from '../../hooks/useTableSettings';
import PartNumber from '../PartNumber';
import SupplierPartNumber from './SupplierPartNumber';
import PartType from '../PartType';
import TableSettingsButton from '../TableSettingsButton';
import CollapsibleSearch from '../CollapsibleSearch';
import VendorSelector from '../VendorSelector';

interface OrderedFullScreenProps {
    tabs: Array<OrderedTab>;
    vin: string;
    onClose: () => void;
    tableSettings: TableSettings;
    onUpdateTableSettings: (newSettings: Partial<TableSettings>) => void;
}

type PartStatus = SupplierOrderStatus | 'returned';

interface FlatOrderedPart extends OrderedPart {
    sectionStatus: SupplierOrderStatus;
    sectionKey: string;
    sectionTitle: string;
    vendorLabel: string;
}

const getStatusColor = (status: PartStatus) => {
    switch (status) {
        case 'confirmed':
            return 'purple';
        case 'ordered':
            return 'teal';
        case 'rejected':
            return 'gray';
        case 'cancelled':
            return 'red';
        case 'returned':
            return 'blue';
    }
};

const getStatusLabel = (status: PartStatus) => {
    switch (status) {
        case 'confirmed':
            return 'Confirmed';
        case 'ordered':
            return 'Ordered';
        case 'rejected':
            return 'Rejected';
        case 'cancelled':
            return 'Cancelled';
        case 'returned':
            return 'Returned';
    }
};

const getPartStatus = (part: FlatOrderedPart): PartStatus => {
    if (part.returned) return 'returned';
    if (part.cancelled) return 'cancelled';
    return part.sectionStatus;
};

const OrderedFullScreen = ({
    tabs,
    vin,
    onClose,
    tableSettings,
    onUpdateTableSettings,
}: OrderedFullScreenProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVendors, setSelectedVendors] = useState<Array<string> | 'all'>('all');
    const tableSettingsSx = useMemo(() => getTableSettingsSx(tableSettings), [tableSettings]);

    const sourceSections = useMemo(() => {
        const allTab = tabs.find((tab) => tab.key === 'all');
        return allTab ? allTab.sections : tabs.flatMap((tab) => tab.sections);
    }, [tabs]);

    const allParts = useMemo(() => {
        const seen = new Set<string>();
        const parts: Array<FlatOrderedPart> = [];

        sourceSections.forEach((section) => {
            section.parts.forEach((part) => {
                const dedupeKey = `${section.key}:${part.partId}`;
                if (seen.has(dedupeKey)) return;
                seen.add(dedupeKey);

                const vendorLabel = (part.vendor || section.title || '').trim();
                parts.push({
                    ...part,
                    sectionStatus: section.status,
                    sectionKey: section.key,
                    sectionTitle: section.title,
                    vendorLabel: vendorLabel || 'Unknown Vendor',
                });
            });
        });

        return parts;
    }, [sourceSections]);

    const vendorOptions = useMemo(() => {
        return Array.from(new Set(allParts.map((part) => part.vendorLabel))).sort((a, b) => a.localeCompare(b));
    }, [allParts]);

    useEffect(() => {
        if (selectedVendors === 'all') return;
        const validSelections = selectedVendors.filter((vendor) => vendorOptions.includes(vendor));
        if (validSelections.length !== selectedVendors.length) {
            setSelectedVendors(validSelections);
        }
    }, [selectedVendors, vendorOptions]);

    const vendorFilteredParts = useMemo(() => {
        if (selectedVendors === 'all') return allParts;
        return allParts.filter((part) => selectedVendors.includes(part.vendorLabel));
    }, [allParts, selectedVendors]);

    const filteredParts = useMemo(() => {
        if (!searchQuery.trim()) return vendorFilteredParts;
        const q = searchQuery.toLowerCase().trim();
        return vendorFilteredParts.filter((part) => {
            const statusLabel = getStatusLabel(getPartStatus(part)).toLowerCase();
            return (
                part.name.toLowerCase().includes(q) ||
                (part.partNumber && part.partNumber.toLowerCase().includes(q)) ||
                (part.correctedPartNumber && part.correctedPartNumber.toLowerCase().includes(q)) ||
                (part.supplierPartNumber && part.supplierPartNumber.toLowerCase().includes(q)) ||
                (part.type && part.type.toLowerCase().includes(q)) ||
                (part.delivery && part.delivery.toLowerCase().includes(q)) ||
                part.vendorLabel.toLowerCase().includes(q) ||
                statusLabel.includes(q)
            );
        });
    }, [searchQuery, vendorFilteredParts]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { scrollContainerProps: stickyCopyScrollProps } = useStickyTableCopyFront(
        scrollContainerRef,
        filteredParts.length,
    );

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
        <Box
            className="partbay-fullscreen-backdrop"
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.500"
            zIndex={1000}
            display="flex"
            alignItems="stretch"
            justifyContent="center"
            pt={3}
            pb={3}
            px={3}
            transition="right 220ms ease"
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <Box
                bg="white"
                borderRadius="xl"
                display="flex"
                flexDirection="column"
                overflow="hidden"
                w="100%"
                boxShadow="none"
                onClick={(event) => event.stopPropagation()}
            >
                <Flex
                    px={6}
                    py={3}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    align="center"
                    justify="space-between"
                    flexShrink={0}
                    bg="white"
                    borderTopRadius="xl"
                >
                    <HStack spacing={3} flex={1} maxW="820px">
                        <Box flex={1}>
                            <CollapsibleSearch
                                isCompact={false}
                                width="100%"
                                placeholder="Search parts, part numbers, vendors..."
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                            />
                        </Box>
                        <VendorSelector
                            vendors={vendorOptions}
                            selectedVendors={selectedVendors}
                            onVendorChange={setSelectedVendors}
                        />
                        {(searchQuery || selectedVendors !== 'all') && (
                            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                                {filteredParts.length} result{filteredParts.length !== 1 ? 's' : ''}
                            </Text>
                        )}
                    </HStack>

                    <HStack spacing={2}>
                        <TableSettingsButton
                            settings={tableSettings}
                            onUpdateSettings={onUpdateTableSettings}
                        />
                        <Tooltip label="Exit full screen" placement="top">
                            <YaxleIconButton
                                aria-label="Exit full screen"
                                icon={<YaxleIcon name="collapse" size="sm" />}
                                size="toolbar"
                                variant="ghost"
                                colorScheme="gray"
                                color="fg.muted"
                                onClick={onClose}
                            />
                        </Tooltip>
                    </HStack>
                </Flex>

                <Box
                    ref={scrollContainerRef}
                    {...stickyCopyScrollProps}
                    flex={1}
                    overflowY="auto"
                    overflowX="auto"
                    sx={{
                        ...tableSettingsSx,
                        ...stickyTableScrollCopySx,
                        '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e0 transparent',
                    }}
                >
                    <Table
                        variant="simple"
                        size="sm"
                        w="100%"
                        minW="1320px"
                        sx={{ 'th:first-of-type, td:first-of-type': { pl: 6 }, 'th:last-of-type, td:last-of-type': { pr: 6 } }}
                    >
                        <Thead position="sticky" top={0} zIndex={STICKY_TABLE_HEADER_Z_INDEX} bg="white">
                            <Tr bg="gray.50">
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="220px">
                                    PART NAME
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="140px">
                                    OE PART NUMBER
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="140px">
                                    SUPPLIER PART NUMBER
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="180px">
                                    VENDOR NAME
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="120px">
                                    DELIVERY ETA
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px" isNumeric>
                                    LIST PRICE
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px" isNumeric>
                                    BUY PRICE
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="120px" isNumeric>
                                    GROSS MARGIN
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px">
                                    STATUS
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredParts.length === 0 ? (
                                <Tr>
                                    <Td colSpan={9} py={12} textAlign="center">
                                        <Text fontSize="lg" color="gray.600" fontWeight="medium">
                                            {searchQuery || selectedVendors !== 'all'
                                                ? 'No ordered parts match your filters'
                                                : 'No ordered parts'}
                                        </Text>
                                    </Td>
                                </Tr>
                            ) : (
                                filteredParts.map((part, partIndex) => {
                                    const inactive = part.cancelled || part.returned;
                                    const rowColor = part.cancelled ? 'red' : part.returned ? 'blue' : null;
                                    const textColor = part.cancelled ? 'red.500' : part.returned ? 'blue.500' : 'gray.900';
                                    const partStatus = getPartStatus(part);

                                    return (
                                        <Tr
                                            key={`${part.sectionKey}-${part.partId}`}
                                            _hover={{ bg: rowColor ? `${rowColor}.50` : 'gray.25' }}
                                            bg={rowColor ? `${rowColor}.50` : undefined}
                                            opacity={inactive ? 0.6 : 1}
                                        >
                                            <Td py={4}>
                                                <VStack align="flex-start" spacing={1}>
                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color={textColor}
                                                        textDecoration={inactive ? 'line-through' : undefined}
                                                    >
                                                        {part.name}
                                                    </Text>
                                                    {part.type ? <PartType partType={part.type} /> : null}
                                                </VStack>
                                            </Td>
                                            <Td py={4}>
                                                <Box textDecoration={inactive ? 'line-through' : undefined} color={rowColor ? `${rowColor}.500` : undefined}>
                                                    <PartNumber
                                                        partNumber={part.partNumber}
                                                        correctedPartNumber={part.correctedPartNumber}
                                                        vin={vin}
                                                       
                                                    />
                                                </Box>
                                            </Td>
                                            <Td py={4}>
                                                <SupplierPartNumber
                                                    partNumber={part.partNumber}
                                                    correctedPartNumber={part.correctedPartNumber}
                                                    supplierPartNumber={part.supplierPartNumber}
                                                    inactive={inactive}
                                                    textColor={textColor}
                                                />
                                            </Td>
                                            <Td py={4}>
                                                <Text fontSize="sm" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                    {part.vendorLabel || '-'}
                                                </Text>
                                            </Td>
                                            <Td py={4}>
                                                {part.correctedDelivery && part.correctedDelivery !== part.delivery ? (
                                                    <VStack align="flex-start" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {part.delivery || '-'}
                                                        </Text>
                                                        <Text
                                                            fontSize="sm"
                                                            color={inactive ? textColor : 'orange.500'}
                                                            fontWeight="medium"
                                                            textDecoration={inactive ? 'line-through' : undefined}
                                                        >
                                                            {part.correctedDelivery}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                        {part.delivery || '-'}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td py={4} isNumeric>
                                                {part.correctedListPrice && part.correctedListPrice !== part.listPrice ? (
                                                    <VStack align="flex-end" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {part.listPrice ? formatPrice(part.listPrice) : '-'}
                                                        </Text>
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            color={inactive ? textColor : 'orange.500'}
                                                            textDecoration={inactive ? 'line-through' : undefined}
                                                        >
                                                            {formatPrice(part.correctedListPrice)}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color={textColor}
                                                        textDecoration={inactive ? 'line-through' : undefined}
                                                    >
                                                        {part.listPrice ? formatPrice(part.listPrice) : '-'}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td py={4} isNumeric>
                                                {part.correctedBuyPrice && part.correctedBuyPrice !== part.buyPrice ? (
                                                    <VStack align="flex-end" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {part.buyPrice ? formatPrice(part.buyPrice) : '-'}
                                                        </Text>
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="medium"
                                                            color={inactive ? textColor : 'orange.500'}
                                                            textDecoration={inactive ? 'line-through' : undefined}
                                                        >
                                                            {formatPrice(part.correctedBuyPrice)}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color={textColor}
                                                        textDecoration={inactive ? 'line-through' : undefined}
                                                    >
                                                        {part.buyPrice ? formatPrice(part.buyPrice) : '-'}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td py={4} isNumeric>
                                                {part.correctedGrossMargin && part.correctedGrossMargin !== part.grossMargin ? (
                                                    <VStack align="flex-end" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {formatPercentage(part.grossMargin)}
                                                        </Text>
                                                        <Text
                                                            fontSize="sm"
                                                            color={inactive ? textColor : 'orange.500'}
                                                            textDecoration={inactive ? 'line-through' : undefined}
                                                        >
                                                            {formatPercentage(part.correctedGrossMargin)}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                        {formatPercentage(part.grossMargin)}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td py={4}>
                                                <Tag
                                                    size="sm"
                                                    colorScheme={getStatusColor(partStatus)}
                                                    variant="subtle"
                                                    borderRadius="md"
                                                    fontWeight="medium"
                                                    fontSize="xs"
                                                >
                                                    {getStatusLabel(partStatus)}
                                                </Tag>
                                            </Td>
                                        </Tr>
                                    );
                                })
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </Box>
        </Box>
    );
};

export default OrderedFullScreen;
